/** @jsxImportSource @emotion/react */
import React, {useState, useEffect} from 'react';
import {useFieldArray, Controller, get, useWatch} from 'react-hook-form';
import SubHeader from 'app/gakki/components/SubHeader';
import Select from 'components/Select';
import {transferDateObj, formatYYYYMMDD} from 'app/gakki/utils/time';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlusCircle} from '@fortawesome/free-solid-svg-icons';
import {IconButton} from '../../../../../components/Button';
import {
  FormItem,
  Input,
  DatePicker,
  ErrorMessage,
  Switch,
} from 'components/form-items';
import Block from './block';
import DependentsShow from './dependents-show';
import DependentsBlock from './dependents-block';
import DependentsIcon from './dependents-icon';
import {DeleteDialog} from '../../../components/Dialog';
import {DateTime} from 'luxon';

const DependentsForm = (props: any) => {
  const {
    formProps,
    defaultValues,
    defaultFields,
    options,
    dependentsFieldName,
    isUnable,
    isError,
    renderErrorMessage,
    index,
    updateLocale,
  } = props;
  const {control, formState} = formProps;
  const dependentsFieldValue = useWatch({
    control,
    name: dependentsFieldName,
    defaultValue: defaultFields,
  });

  return (
    <DependentsBlock>
      <FormItem
        label="眷屬姓名"
        itemName="incomeTax_dependents_name"
        required
        width="100px"
        data-test-id={'name'}
      >
        <Controller
          render={({onChange, value}) => (
            <Input
              value={value}
              disabled={formState.isSubmitting || isUnable}
              onChange={(e: any) => {
                onChange(e);
                updateLocale();
              }}
              maxLength={20}
              error={isError(`${dependentsFieldName}.${index}.name`)}
            />
          )}
          name={`${dependentsFieldName}.${index}.name`}
          control={control}
          rules={{required: !isUnable && '眷屬姓名為必填'}}
          defaultValue={defaultValues.name}
        />
        {renderErrorMessage(`${dependentsFieldName}.${index}.name`)}
      </FormItem>
      <FormItem
        label="稱謂"
        itemName="incomeTax_dependents_dependentsTitle"
        required
        width="100px"
        data-test-id={'dependents-title'}
      >
        <Controller
          render={({onChange, value}) => (
            <Input
              value={value}
              disabled={formState.isSubmitting || isUnable}
              onChange={(e: any) => {
                onChange(e);
                updateLocale();
              }}
              maxLength={10}
              error={isError(`${dependentsFieldName}.${index}.dependentsTitle`)}
            />
          )}
          name={`${dependentsFieldName}.${index}.dependentsTitle`}
          control={control}
          rules={{required: !isUnable && '稱謂為必填'}}
          defaultValue={defaultValues.dependentsTitle}
        />
        {renderErrorMessage(`${dependentsFieldName}.${index}.dependentsTitle`)}
      </FormItem>
      <FormItem
        label="生日"
        itemName="incomeTax_dependents_birthday"
        required
        width="100px"
        data-test-id={'birthday'}
      >
        <Controller
          render={({onChange, value}) => (
            <DatePicker
              dateFormat="yyyy/MM/dd"
              selected={transferDateObj(value)}
              disabled={formState.isSubmitting || isUnable}
              onChange={(date: Date) => {
                onChange(formatYYYYMMDD(date));
                updateLocale();
              }}
              error={isError(`${dependentsFieldName}.${index}.birthday`)}
            />
          )}
          name={`${dependentsFieldName}.${index}.birthday`}
          control={control}
          rules={{
            required: !isUnable && '生日為必填',
          }}
          defaultValue={defaultValues.birthday}
        />
        {renderErrorMessage(`${dependentsFieldName}.${index}.birthday`)}
      </FormItem>
      <FormItem
        label="證件號碼"
        itemName="incomeTax_dependents_identityNo"
        required
        width="130px"
        data-test-id={'identity-no'}
      >
        <Controller
          render={({onChange, value}) => (
            <Input
              value={value}
              disabled={formState.isSubmitting || isUnable}
              onChange={(e: any) => {
                onChange(e);
                updateLocale();
              }}
              maxLength={20}
              error={isError(`${dependentsFieldName}.${index}.identityNo`)}
            />
          )}
          name={`${dependentsFieldName}.${index}.identityNo`}
          control={control}
          rules={{
            required: !isUnable && '證件號碼為必填',
            validate: v => {
              return !isUnable &&
                dependentsFieldValue.some(
                  (f: any, compareIdx: number) =>
                    f.identityNo === v && compareIdx !== index,
                )
                ? '證件號碼不可重覆'
                : true;
            },
          }}
          defaultValue={defaultValues.identityNo}
        />
        {renderErrorMessage(`${dependentsFieldName}.${index}.identityNo`)}
      </FormItem>
      <FormItem
        label="符合條件"
        itemName="incomeTax_dependents_dependentsExemptionConditionCode"
        required
        width="220px"
        data-test-id={'dependents-exemption-condition-code'}
      >
        <Controller
          render={({onChange, value}) => (
            <Select
              menuPlacement="auto"
              value={
                options.dependentsExemptionCondition.find(
                  (o: any) => o.value === value,
                ) || null
              }
              options={options.dependentsExemptionCondition}
              onChange={(o: {value: string}) => {
                onChange(o.value);
                updateLocale();
              }}
              isDisabled={formState.isSubmitting || isUnable}
              getOptionLabel={(o: any) => o.item}
              error={isError(
                `${dependentsFieldName}.${index}.dependentsExemptionConditionCode`,
              )}
            />
          )}
          name={`${dependentsFieldName}.${index}.dependentsExemptionConditionCode`}
          control={control}
          rules={{required: !isUnable && '符合條件為必填'}}
          defaultValue={defaultValues.dependentsExemptionConditionCode}
        />
      </FormItem>
      <FormItem
        label="現居地址"
        itemName="incomeTax_dependents_address"
        required
        width="270px"
        data-test-id={'address'}
      >
        <Controller
          render={({onChange, value}) => (
            <Input
              value={value}
              disabled={formState.isSubmitting || isUnable}
              onChange={(e: any) => {
                onChange(e);
                updateLocale();
              }}
              maxLength={50}
              error={isError(`${dependentsFieldName}.${index}.address`)}
            />
          )}
          name={`${dependentsFieldName}.${index}.address`}
          control={control}
          rules={{required: !isUnable && '現居地址為必填'}}
          defaultValue={defaultValues.address}
        />
        {renderErrorMessage(`${dependentsFieldName}.${index}.address`)}
      </FormItem>
    </DependentsBlock>
  );
};
const IncomeTaxDependentsData = (props: any) => {
  const {formProps, defaultValues, options, updateLocale, defaultTaxEnabled} =
    props;
  const {control, formState, errors, trigger} = formProps;
  const dependentsFieldName = `dependentsTax.dependentsTaxItems`;
  const dependentsField = useFieldArray({
    control: formProps.control,
    name: dependentsFieldName,
    keyName: 'dependentsTaxId',
  });
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
  const isTaxUnable = !useWatch({
    control,
    name: `tax.enabled`,
    defaultValue: defaultTaxEnabled,
  });
  const isUnable = !useWatch({
    control,
    name: `dependentsTax.enabled`,
    defaultValue: defaultValues.enabled,
  });
  const isError = (name: string) =>
    !isUnable && formState.isSubmitted && get(errors, name);
  const renderErrorMessage = (name: string) =>
    isError(name) && <ErrorMessage>{get(errors, name)?.message}</ErrorMessage>;

  useEffect(() => {
    if (!isUnable && formState.isSubmitted) {
      trigger();
    }
  }, [isUnable, formState.isSubmitted]);
  return (
    <div data-test-id={'income-tax-dependents'}>
      <SubHeader>
        <span style={{paddingRight: '15px'}}>所得稅扶養親屬</span>
        <Controller
          render={({onChange, value}) => (
            <div id={'enable-dependents-tax'} className="flex items-center">
              <Switch
                checked={value}
                onChange={(e: any) => {
                  onChange(e);
                  updateLocale();
                }}
                disabled={isTaxUnable}
              />
            </div>
          )}
          name={`dependentsTax.enabled`}
          control={control}
          defaultValue={defaultValues.enabled}
        />
      </SubHeader>
      <Block padding="0 20px">
        {dependentsField.fields.map((f: any, index: number) => {
          return (
            <DependentsShow
              key={f.dependentsTaxId}
              isShow
              data-test-id={`income-tax-dependent-${index}`}
            >
              <DependentsForm
                {...props}
                options={options}
                defaultValues={f}
                defaultFields={defaultValues.dependentsTaxItems}
                dependentsFieldName={dependentsFieldName}
                isUnable={isUnable}
                isError={isError}
                renderErrorMessage={renderErrorMessage}
                index={index}
              />
              <DependentsIcon>
                {index === 0 && (
                  <IconButton
                    onClick={() => {
                      dependentsField.append({
                        name: null,
                        dependentsTitle: null,
                        birthday: null,
                        identityNo: null,
                        address: null,
                        dependentsExemptionConditionCode: null,
                      });
                      updateLocale();
                    }}
                    disabled={isUnable}
                    data-test-id={'add-btn'}
                  >
                    <FontAwesomeIcon
                      icon={faPlusCircle}
                      style={{fontSize: 28}}
                    />
                  </IconButton>
                )}
                {dependentsField.fields.length > 1 && (
                  <IconButton
                    onClick={e => {
                      e.preventDefault();
                      setDeleteIdx(index);
                      updateLocale();
                    }}
                    disabled={isUnable}
                    data-test-id={'remove-btn'}
                  >
                    <FontAwesomeIcon
                      icon={['fal', 'trash-alt']}
                      style={{fontSize: 18}}
                    />
                  </IconButton>
                )}
              </DependentsIcon>
            </DependentsShow>
          );
        })}
        <DeleteDialog
          isOpen={deleteIdx !== null}
          onCancel={() => setDeleteIdx(null)}
          onConfirm={() => {
            if (deleteIdx !== null) {
              dependentsField.remove(deleteIdx);
            }
            updateLocale();
            setDeleteIdx(null);
          }}
        />
      </Block>
    </div>
  );
};

export default IncomeTaxDependentsData;
